import UsefulLinks from '../components/UsefulLinks';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Bike, Sailboat, Wine, UtensilsCrossed, Camera, TreePine } from 'lucide-react';
import SEO from '../components/SEO';

const ActivitesIleDeRe = () => {
  const activites = [
    {
      icon: <Bike className="w-12 h-12" />,
      titre: "Vélo - 100 km de Pistes",
      description: "L'Île de Ré est LE paradis du cyclotourisme en France. Plus de 100 km de pistes cyclables sécurisées traversent villages, marais salants, forêts de pins et vignobles. Location disponible partout sur l'île.",
      niveau: "Tous niveaux",
      duree: "1h à une journée",
      prix: "Location 10-15€/jour"
    },
    {
      icon: <Sailboat className="w-12 h-12" />,
      titre: "Voile & Sports Nautiques",
      description: "Profitez des conditions exceptionnelles de l'île pour la voile, le paddle, le kayak de mer, le kitesurf et la planche à voile. Écoles et locations disponibles dans chaque village côtier.",
      niveau: "Débutant à expert",
      duree: "2h à une journée",
      prix: "À partir de 25€/session"
    },
    {
      icon: <Wine className="w-12 h-12" />,
      titre: "Découverte des Vignobles",
      description: "Visitez les vignobles de l'île et dégustez les vins locaux (Pineau des Charentes, Cognac). Plusieurs domaines proposent des visites guidées avec dégustation de produits du terroir.",
      niveau: "Tous publics",
      duree: "2-3 heures",
      prix: "Gratuit à 15€"
    },
    {
      icon: <UtensilsCrossed className="w-12 h-12" />,
      titre: "Gastronomie & Marchés",
      description: "Découvrez les huîtres de l'île, le sel de Ré, les pommes de terre AOP, et les produits de la mer. Marchés quotidiens dans chaque village, cabanes ostréicoles au port.",
      niveau: "Tous publics",
      duree: "Matinée",
      prix: "Variable"
    },
    {
      icon: <Camera className="w-12 h-12" />,
      titre: "Phare des Baleines",
      description: "Grimpez les 257 marches du phare des Baleines pour une vue panoramique à 360° sur l'île et l'océan. Musée et vieille tour inclus dans la visite.",
      niveau: "Accessible",
      duree: "1-2 heures",
      prix: "3,50€ adulte"
    },
    {
      icon: <TreePine className="w-12 h-12" />,
      titre: "Marais Salants",
      description: "Visitez les marais salants traditionnels et découvrez le métier de saunier. Visite guidée, ramassage de sel, dégustation. Une expérience authentique et pédagogique.",
      niveau: "Familial",
      duree: "1h30",
      prix: "8-12€"
    }
  ];

  return (
    <>
      <SEO 
        title="Que Faire à l'Île de Ré ? Top Activités et Loisirs 2026"
        description="Découvrez les meilleures activités de l'Île de Ré : vélo, voile, sports nautiques, vignobles, marchés, phare des Baleines. Guide complet pour un séjour inoubliable."
        keywords="activités Île de Ré, que faire Île de Ré, vélo Île de Ré, voile Ré, phare Baleines, marais salants, vignobles Ré"
        ogImage="https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=1200"
        url="/activites-ile-de-re"
      />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/seo/activites.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Activités & Loisirs à l'Île de Ré
            </h1>
            <p className="text-lg sm:text-xl text-gray-200">
              Vélo, voile, gastronomie, culture - Une île aux mille découvertes
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction */}
        <section className="mb-16 prose prose-lg max-w-none">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            L'<strong>Île de Ré</strong> offre une variété exceptionnelle d'<strong>activités pour tous les goûts</strong> : 
            sportives, culturelles, gastronomiques ou nature. Que vous soyez en famille, en couple ou entre amis, 
            vous trouverez de quoi remplir vos journées entre détente et découvertes.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Depuis notre <strong>Villa Nomade</strong>, toutes ces activités sont à portée de vélo. 
            Voici notre sélection des incontournables pour un séjour réussi sur l'île.
          </p>
        </section>

        {/* Activités principales */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Les incontournables</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {activites.map((activite, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                <div className="text-[#c17a3a] mb-4">{activite.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{activite.titre}</h3>
                <p className="text-gray-700 mb-4">{activite.description}</p>
                <div className="space-y-1 text-sm text-gray-600">
                  <div><strong>Niveau :</strong> {activite.niveau}</div>
                  <div><strong>Durée :</strong> {activite.duree}</div>
                  <div><strong>Tarif :</strong> {activite.prix}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Villages à visiter */}
        <section className="mb-16 bg-amber-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Les villages à ne pas manquer</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">Saint-Martin-de-Ré ⭐</h3>
              <p className="text-gray-700">
                Capitale de l'île classée UNESCO. Fortifications Vauban, port animé, boutiques de créateurs, 
                restaurants gastronomiques. Marché quotidien incontournable.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Ars-en-Ré</h3>
              <p className="text-gray-700">
                Village pittoresque avec son clocher noir et blanc. Marché nocturne l'été, galeries d'art, 
                cabanes ostréicoles au port. Ambiance authentique préservée.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">La Flotte</h3>
              <p className="text-gray-700">
                Labellisé "Plus Beaux Villages de France". Ruelles fleuries, marché médiéval l'été, 
                abbaye des Châteliers (ruines cistercienne du XIIe siècle).
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Les Portes-en-Ré</h3>
              <p className="text-gray-700">
                Village de pêcheurs authentique, port animé, marché nocturne réputé. Point de départ idéal 
                pour explorer le nord de l'île à vélo.
              </p>
            </div>
          </div>
        </section>

        {/* Activités par saison */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Activités selon la saison</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-yellow-500 pl-6">
              <h3 className="text-xl font-semibold mb-2">🌸 Printemps (Avril-Juin)</h3>
              <p className="text-gray-700">
                Vélo dans la nature en fleurs, observation d'oiseaux migrateurs, ouverture progressive des sites touristiques. 
                Températures douces, île calme et authentique.
              </p>
            </div>
            <div className="border-l-4 border-orange-500 pl-6">
              <h3 className="text-xl font-semibold mb-2">☀️ Été (Juillet-Août)</h3>
              <p className="text-gray-700">
                Baignade, sports nautiques, marchés nocturnes, festivals et concerts. Animation maximale, 
                vie culturelle intense. Réservation anticipée fortement recommandée.
              </p>
            </div>
            <div className="border-l-4 border-amber-600 pl-6">
              <h3 className="text-xl font-semibold mb-2">🍂 Automne (Septembre-Novembre)</h3>
              <p className="text-gray-700">
                Vendanges et dégustations, récolte du sel, gastronomie (huîtres en saison). 
                Lumière dorée exceptionnelle, températures encore agréables jusqu'en octobre.
              </p>
            </div>
            <div className="border-l-4 border-blue-500 pl-6">
              <h3 className="text-xl font-semibold mb-2">❄️ Hiver (Décembre-Mars)</h3>
              <p className="text-gray-700">
                Balades ressourçantes sur plages désertes, observation tempêtes océaniques, 
                dégustations huîtres au coin du feu. Île authentique, tarifs avantageux.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#c17a3a] to-[#a66830] text-white p-12 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">Votre Base Idéale pour Explorer l'Île</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Villa Nomade - Située au cœur de l'île, à vélo de toutes les activités. 
            Spa de nage pour vous détendre après vos journées d'aventure.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#c17a3a] hover:bg-gray-100">
                Réserver maintenant
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Découvrir la villa
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          "name": "Activités Île de Ré",
          "description": "Guide complet des activités et loisirs sur l'Île de Ré : vélo, voile, gastronomie, culture",
          "url": "https://villa-nomade-re.preview.emergentagent.com/activites-ile-de-re"
        })}
      </script>
    </>
  );
};

      <UsefulLinks />
export default ActivitesIleDeRe;
